import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "./ui/select";
import { Image as ImageIcon, Sparkles, RefreshCw, Check } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * <AiImage> — pulsante riutilizzabile per generare immagini con AI.
 *
 * Props:
 *  - onGenerated: (url, id) => void  → callback quando l'utente conferma
 *  - promptHint:  string             → placeholder
 *  - defaultPrompt: string
 *  - testid: string
 *  - triggerLabel: string
 */
export default function AiImage({
  onGenerated,
  promptHint = "Descrivi l'immagine (es. Kebab classico, ambiente caldo, angolo alto)",
  defaultPrompt = "",
  testid = "ai-image",
  triggerLabel = "Genera immagine con AI",
}) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [model, setModel] = useState("nano-banana");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { url, id }

  const run = async () => {
    if (!prompt.trim()) return toast.error("Scrivi cosa vuoi generare");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/image", { prompt, model });
      setResult({ url: `${API_BASE}${data.url}`, id: data.id, size: data.size_bytes });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const apply = () => {
    if (!result) return;
    // Passiamo l'URL pubblico che il caller può salvare come image_url del prodotto/premio
    if (onGenerated) onGenerated(`${API_BASE}/api/ai/image/${result.id}`, result.id);
    setOpen(false);
    setResult(null);
    setPrompt(defaultPrompt);
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="border-fuchsia-500 text-fuchsia-700 dark:text-fuchsia-300 hover:bg-fuchsia-500/10"
        onClick={() => setOpen(true)}
        data-testid={testid}
      >
        <Sparkles className="h-3.5 w-3.5 mr-1" /> {triggerLabel}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o && !prompt) setPrompt(defaultPrompt || ""); }}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-xl" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-fuchsia-300" /> Generazione immagine AI</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-400 text-xs mb-1 inline-block">Modello</Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger data-testid={`${testid}-model`} className="bg-zinc-950 border-zinc-800 h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nano-banana">Gemini Nano Banana (veloce)</SelectItem>
                  <SelectItem value="gpt-image-1">GPT Image 1 (alta qualità)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1 inline-block">Prompt</Label>
              <Textarea data-testid={`${testid}-prompt`}
                value={prompt} onChange={(e) => setPrompt(e.target.value)}
                placeholder={promptHint} rows={3}
                className="bg-zinc-950 border-zinc-800" />
            </div>
            <Button data-testid={`${testid}-run`} onClick={run} disabled={loading}
              className="bg-fuchsia-600 hover:bg-fuchsia-700">
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {loading ? "Genero…" : "Genera immagine"}
            </Button>
            {result && (
              <div className="space-y-2">
                <div className="rounded-lg overflow-hidden border border-zinc-800 bg-black">
                  <img src={result.url} alt="AI generated" data-testid={`${testid}-preview`}
                    className="w-full max-h-[50vh] object-contain" />
                </div>
                <div className="flex justify-end">
                  <Button data-testid={`${testid}-apply`} onClick={apply}
                    className="bg-emerald-600 hover:bg-emerald-700">
                    <Check className="h-4 w-4 mr-1" /> Usa questa immagine
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
